import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';

export interface DailyLogReviewPhoto {
  id?: string;
  url: string;
  caption?: string | null;
  fileName?: string | null;
}

export interface DailyLogReviewModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  projectName?: string;
  logDate?: string;
  bodyTitle?: string;
  bodyLines?: string[];
  bodyText?: string;
  photos: DailyLogReviewPhoto[];
  visibilityLabel?: string;
  submitterLabel?: string;
  confirmLabel: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function DailyLogReviewModal({
  open,
  title,
  subtitle,
  projectName,
  logDate,
  bodyTitle,
  bodyLines,
  bodyText,
  photos,
  visibilityLabel,
  submitterLabel,
  confirmLabel,
  cancelLabel = 'Back / Edit',
  loading = false,
  onConfirm,
  onCancel,
}: DailyLogReviewModalProps) {
  if (!open) return null;

  const hasBody = Boolean(bodyText?.trim()) || (bodyLines && bodyLines.some((l) => l.trim()));

  return (
    <div className="fixed inset-0 z-[85] flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={loading ? undefined : onCancel} />
      <div className="relative bg-white w-full md:max-w-lg md:mx-4 rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-[#1A1A1A] font-bold text-base">{title}</h3>
          {subtitle && <p className="text-[#6B7280] text-sm mt-1">{subtitle}</p>}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {(projectName || logDate) && (
            <div className="bg-[#F8FAFC] rounded-xl px-3 py-2.5 border border-gray-100 space-y-1">
              {projectName && (
                <p className="text-sm font-semibold text-[#1A1A1A]">{projectName}</p>
              )}
              {logDate && <p className="text-xs text-[#6B7280]">{logDate}</p>}
            </div>
          )}

          {submitterLabel && (
            <div>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Submitter</p>
              <p className="text-sm text-[#374151]">{submitterLabel}</p>
            </div>
          )}

          {visibilityLabel && (
            <div>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Visibility</p>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#F0FDF4] text-[#059669]">
                <i className="ri-eye-line" />
                {visibilityLabel}
              </span>
            </div>
          )}

          {hasBody && (
            <div>
              {bodyTitle && (
                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1.5">{bodyTitle}</p>
              )}
              {bodyText ? (
                <p className="text-sm text-[#374151] leading-relaxed whitespace-pre-wrap">{bodyText}</p>
              ) : (
                <div className="space-y-2">
                  {(bodyLines || []).filter((l) => l.trim()).map((line, idx) => (
                    <p key={idx} className="text-sm text-[#374151] leading-relaxed whitespace-pre-wrap">{line}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
              Photos ({photos.length})
            </p>
            {photos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {photos.map((photo, idx) => (
                  <div key={photo.id || idx} className="border border-gray-100 rounded-xl overflow-hidden bg-white">
                    <div className="aspect-[4/3]">
                      <ProjectPhotoImage
                        src={photo.url}
                        alt={photo.caption || photo.fileName || 'Photo'}
                        sizes="(max-width: 768px) 45vw, 160px"
                        loading="lazy"
                        debugContext="DailyLogReviewModal"
                      />
                    </div>
                    {(photo.caption || photo.fileName) && (
                      <p className="text-[10px] text-[#6B7280] px-2 py-1.5 line-clamp-2">
                        {photo.caption || photo.fileName}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#9CA3AF] italic">No photos attached.</p>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3 rounded-xl border border-gray-200 text-[#374151] text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-3 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Submitting...
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
